import { finite } from "@/lib/science/units";

type Field = "normality" | "density" | "assay" | "ew";
export interface NormalityInput {
  /** normality, eq/L (N) */
  normality: number | null;
  /** density of the concentrated reagent, g/mL */
  density: number | null;
  /** assay / purity, percent */
  assay: number | null;
  /** equivalent weight, g/eq */
  ew: number | null;
}

// Normality of a concentrated liquid reagent:
//   N = (10 × density × assay%) / equivalentWeight
// The factor 10 converts g/mL and % into g per litre.
// Exactly one field must be null (the unknown to solve for).
export function solveNormality(known: NormalityInput): number | null {
  const nulls = (["normality", "density", "assay", "ew"] as Field[]).filter(
    (k) => known[k] === null,
  );
  if (nulls.length !== 1) return null;
  const vals = [known.normality, known.density, known.assay, known.ew].filter(
    (n): n is number => n !== null,
  );
  if (!finite(...vals)) return null;

  const { normality, density, assay, ew } = known;
  switch (nulls[0]) {
    case "normality":
      return ew === 0 ? null : (10 * density! * assay!) / ew!;
    case "density":
      return assay === 0 ? null : (normality! * ew!) / (10 * assay!);
    case "assay":
      return density === 0 ? null : (normality! * ew!) / (10 * density!);
    case "ew":
      return normality === 0 ? null : (10 * density! * assay!) / normality!;
    default:
      return null;
  }
}
